// TanStack Query hooks for cardio sessions (U6 of the
// cardio-progress-from-fit plan). Backed by the GET endpoints shipped in U4:
//   - `GET /api/fit/sessions?kind=run|padel[&limit=N&since=ISO]`
//   - `GET /api/fit/sessions/:id`
//
// Cache keys are tuple-shaped (`['cardio-sessions', kind]`) so the U5 upload
// mutation's `qc.invalidateQueries({ queryKey: ['cardio-sessions', 'padel'] })`
// hits these reads correctly. The detail key is keyed only on `id` so an HR
// sparkline is loaded once per session and shared across mounts.

import { useQuery } from '@tanstack/react-query';

import { apiClient } from './api';
import { STALE_TIME_DAILY, STALE_TIME_SLOW } from './query';
import {
  CardioSessionDetailSchema,
  CardioSessionSummariesSchema,
  type CardioSessionDetail,
  type CardioSessionSummary,
} from './types';

export type CardioKind = 'run' | 'padel';

export type CardioSessionsOptions = {
  limit?: number;
  /** ISO timestamp or ms-epoch string. Server defaults to (now - 90d). */
  since?: string;
};

function buildSessionsUrl(kind: CardioKind, opts: CardioSessionsOptions): string {
  const params = new URLSearchParams({ kind });
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.since) params.set('since', opts.since);
  return `/api/fit/sessions?${params.toString()}`;
}

/**
 * List recent cardio sessions of the given kind. Cache key includes only
 * non-default option fields so common calls (`useCardioSessions('padel', {
 * limit: 8 })`) reuse the cache across mounts.
 */
export function useCardioSessions(
  kind: CardioKind,
  opts: CardioSessionsOptions = {},
) {
  // Stable cache shape: include the options object only when it carries data,
  // otherwise the tuple stays `['cardio-sessions', kind]` to match U5's
  // invalidate keys.
  const hasOpts = opts.limit != null || opts.since != null;
  const keyOpts: Record<string, unknown> = {};
  if (opts.limit != null) keyOpts.limit = opts.limit;
  if (opts.since) keyOpts.since = opts.since;
  const queryKey = hasOpts
    ? (['cardio-sessions', kind, keyOpts] as const)
    : (['cardio-sessions', kind] as const);

  return useQuery<CardioSessionSummary[]>({
    queryKey,
    queryFn: () =>
      apiClient.get<CardioSessionSummary[]>(buildSessionsUrl(kind, opts), {
        schema: CardioSessionSummariesSchema,
      }),
    staleTime: STALE_TIME_DAILY,
  });
}

/**
 * Lazy-fetch a single cardio session's full detail (summary + HR/pace series).
 * Disabled when `id` is null so a card can mount and only load the heavier
 * payload once it's actually visible.
 */
export function useCardioSessionDetail(id: number | null) {
  return useQuery<CardioSessionDetail>({
    queryKey: ['cardio-session', id] as const,
    queryFn: () =>
      apiClient.get<CardioSessionDetail>(`/api/fit/sessions/${id}`, {
        schema: CardioSessionDetailSchema,
      }),
    enabled: id !== null,
    staleTime: STALE_TIME_SLOW,
  });
}

// ---- Week math (padel streak) ----
//
// Mon-first week buckets. A date's "week id" is its ISO Monday in
// `YYYY-MM-DD`. The U6 padel streak walks backwards from THIS week's Monday;
// see PadelStreakCard for the full rule.

/** Returns the YYYY-MM-DD of the Monday of the week containing `ymd` (local). */
export function mondayOf(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) throw new Error(`Invalid ISO date: ${ymd}`);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // JS getDay(): 0 Sun .. 6 Sat. Convert so Mon=0, Sun=6.
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/** Returns the Monday of the week containing today (local). */
export function mondayOfToday(): string {
  const now = new Date();
  const y = now.getFullYear();
  const mo = String(now.getMonth() + 1).padStart(2, '0');
  const da = String(now.getDate()).padStart(2, '0');
  return mondayOf(`${y}-${mo}-${da}`);
}

/** Shift a Monday-ymd back by N weeks. */
export function shiftMondayWeeks(mondayYmd: string, weeks: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(mondayYmd);
  if (!m) throw new Error(`Invalid ISO date: ${mondayYmd}`);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + weeks * 7);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/**
 * Padel weekly streak.
 *
 * Rule (from the plan): walk backwards from this week's Monday.
 *   - If this week has ≥1 padel, count it and continue to the previous week.
 *   - If this week has 0, DO NOT break — continue to last week.
 *   - From the first PRIOR week (not this week) without padel, stop.
 *
 * So a streak of N means: at minimum, the previous N completed weeks (i.e.
 * weeks ending before this Monday) each had ≥1 padel; this week may or may
 * not be in yet.
 *
 * `padelDates` is the list of YYYY-MM-DD dates where a padel row exists.
 */
export function computePadelStreak(padelDates: string[]): number {
  if (padelDates.length === 0) return 0;
  // Pre-bucket dates by their week's Monday for O(N) scanning.
  const weeksWithPadel = new Set<string>();
  for (const d of padelDates) {
    try {
      weeksWithPadel.add(mondayOf(d));
    } catch {
      // ignore malformed
    }
  }
  const thisMon = mondayOfToday();
  let count = 0;
  let cursor = thisMon;
  // Special-case this week: it can be empty without breaking the streak.
  if (weeksWithPadel.has(cursor)) {
    count += 1;
  }
  // Walk backwards through prior completed weeks.
  cursor = shiftMondayWeeks(cursor, -1);
  while (weeksWithPadel.has(cursor)) {
    count += 1;
    cursor = shiftMondayWeeks(cursor, -1);
  }
  return count;
}

export function thisWeekHasPadel(padelDates: string[]): boolean {
  const thisMon = mondayOfToday();
  for (const d of padelDates) {
    try {
      if (mondayOf(d) === thisMon) return true;
    } catch {
      // ignore malformed
    }
  }
  return false;
}

/**
 * The last `weeks` weeks as played/missed booleans, oldest→newest, ending with
 * THIS week (the rightmost entry). Drives the consistency dot row on the padel
 * card so the streak is visible as a pattern, not just a number.
 */
export function recentPadelWeeks(padelDates: string[], weeks: number): boolean[] {
  const weeksWithPadel = new Set<string>();
  for (const d of padelDates) {
    try {
      weeksWithPadel.add(mondayOf(d));
    } catch {
      // ignore malformed
    }
  }
  const thisMon = mondayOfToday();
  const out: boolean[] = [];
  for (let i = weeks - 1; i >= 0; i -= 1) {
    out.push(weeksWithPadel.has(shiftMondayWeeks(thisMon, -i)));
  }
  return out;
}
